import { ResponsiveBar, BarDatum, BarTooltipProps } from "@nivo/bar";
import { useWindowSize } from "hooks/useWindowSize";
import { formatAxisValue } from "components/BooksHomePage/ChartComponents";
import { formatAmount } from "utils/foramtAmount";
import { colors } from "constants/colors";
import {
  ANALYSIS_MODE,
  CustomReportChartResponse,
  SERIES_MODE,
} from "store/apis/customReports";
import { RefObject } from "react";

const MAX_SERIES = 5;
const OTHER_KEY = "Other";
const POS_SUFFIX = "__pos";
const NEG_SUFFIX = "__neg";

export type BarChartSeriesItem = {
  name: string;
  data: number[];
};

export type BarChartProps = {
  xLabels: string[];
  series: BarChartSeriesItem[];
  isStacked?: boolean;
  isPercentage?: boolean;
  showOther?: boolean;
  categoryColor: string[];
  containerRef?: RefObject<HTMLDivElement | null>;
};

const formatSignedAxisValue = (value: number): string => {
  const formatted = formatAxisValue(value);
  return value < 0 ? `-${formatted}` : formatted;
};

const TooltipContent = ({
  color,
  label,
  formattedValue,
}: {
  color: string;
  label: string;
  formattedValue: string;
}) => (
  <div className="t-bg-neutral-100 t-rounded-lg t-px-3 t-py-2 t-text-body-sm t-text-white t-flex t-flex-col t-gap-0.5 t-shadow-lg t-min-w-52">
    <div className="t-flex t-items-center t-gap-1.5">
      <span
        className="t-h-2 t-w-2 t-rounded-full t-flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span>
        {label}: {formattedValue}
      </span>
    </div>
  </div>
);

const Tooltip = ({ id, value, color }: BarTooltipProps<BarDatum>) => (
  <TooltipContent
    color={color}
    label={String(id)}
    formattedValue={formatAmount(Number(value))}
  />
);

const PercentageTooltip = ({ id, value, color }: BarTooltipProps<BarDatum>) => (
  <TooltipContent
    color={color}
    label={String(id)}
    formattedValue={`${Number(value).toFixed(1)}%`}
  />
);
const resolveSeriesName = (key: string): string => {
  if (key.endsWith(POS_SUFFIX)) return key.slice(0, -POS_SUFFIX.length);
  if (key.endsWith(NEG_SUFFIX)) return key.slice(0, -NEG_SUFFIX.length);
  return key;
};

const buildStackedNivoData = (
  xLabels: string[],
  series: BarChartSeriesItem[],
  showOther: boolean
): {
  nivoData: BarDatum[];
  keys: string[];
  otherSeriesNames: string[];
  otherSeriesData: BarChartSeriesItem[];
} => {
  const seriesWithTotals = series.map((s) => ({
    ...s,
    total: s.data.reduce((sum, v) => sum + Math.abs(v), 0),
  }));
  seriesWithTotals.sort((a, b) => b.total - a.total);

  const topSeries = seriesWithTotals.slice(0, MAX_SERIES);
  const otherSeries = seriesWithTotals.slice(MAX_SERIES);
  const hasOther = showOther && otherSeries.length > 0;

  const positiveKeys = topSeries.map((s) => `${s.name}${POS_SUFFIX}`);
  const negativeKeys = topSeries.map((s) => `${s.name}${NEG_SUFFIX}`);
  const allKeys = [
    ...negativeKeys,
    ...(hasOther ? [OTHER_KEY] : []),
    ...positiveKeys,
  ];

  const nivoData: BarDatum[] = xLabels.map((xLabel, xIndex) => {
    const datum: BarDatum = { xLabel };

    topSeries.forEach((s) => {
      const value = s.data[xIndex] ?? 0;
      datum[`${s.name}${POS_SUFFIX}`] = value >= 0 ? value : 0;
      datum[`${s.name}${NEG_SUFFIX}`] = value < 0 ? value : 0;
    });

    if (hasOther) {
      datum[OTHER_KEY] = otherSeries.reduce(
        (sum, s) => sum + (s.data[xIndex] ?? 0),
        0
      );
    }

    return datum;
  });

  return {
    nivoData,
    keys: allKeys,
    otherSeriesNames: otherSeries.map((s) => s.name),
    otherSeriesData: hasOther ? otherSeries : [],
  };
};

const getNiceTickInterval = (value: number): number => {
  const absValue = Math.abs(value);
  if (absValue <= 0) return 100;
  const magnitude = Math.pow(10, Math.floor(Math.log10(absValue)));
  const normalized = absValue / magnitude;
  if (normalized <= 2) return magnitude;
  if (normalized <= 5) return magnitude * 2;
  return magnitude * 5;
};

const generateTickValues = (
  min: number,
  max: number,
  interval: number
): number[] => {
  const ticks: number[] = [];
  for (let i = min; i <= max; i += interval) {
    ticks.push(i);
  }
  if (!ticks.includes(0)) ticks.push(0);
  return ticks.sort((a, b) => a - b);
};

const makeStackedTooltip =
  (
    isPercentage: boolean,
    otherSeriesData: BarChartSeriesItem[],
    xLabels: string[]
  ) =>
  ({ id, value, indexValue, color }: BarTooltipProps<BarDatum>) => {
    const seriesName = resolveSeriesName(String(id));
    const isOther = seriesName === OTHER_KEY;

    if (isOther && otherSeriesData.length > 0) {
      const xIndex = xLabels.indexOf(String(indexValue));
      const breakdown = otherSeriesData.map((s) => ({
        name: s.name,
        value: s.data[xIndex] ?? 0,
      }));

      return (
        <div className="t-bg-neutral-100 t-rounded-lg t-px-3 t-py-2 t-text-body-sm t-text-white t-flex t-flex-col t-gap-1 t-shadow-lg t-min-w-60">
          <div className="t-flex t-items-center t-gap-1.5">
            <span
              className="t-h-2 t-w-2 t-rounded-full t-flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="t-font-medium">Others:</span>
          </div>
          <div className="t-flex t-flex-col t-gap-0.5 t-pl-3.5">
            {breakdown?.map((item) => (
              <span key={item.name} className="t-text-white t-text-caption">
                {item.name}:{" "}
                {isPercentage
                  ? `${item.value.toFixed(1)}%`
                  : formatAmount(item.value)}
              </span>
            ))}
          </div>
        </div>
      );
    }

    const formattedValue = isPercentage
      ? `${Number(value).toFixed(1)}%`
      : formatAmount(Number(value));

    return (
      <TooltipContent
        color={color}
        label={seriesName}
        formattedValue={formattedValue}
      />
    );
  };

// Vertical labels so period names don't overlap on narrow screens.
const MOBILE_TICK_ROTATION = -90;

export const BarChart = ({
  xLabels,
  series,
  isStacked = false,
  isPercentage = false,
  showOther = false,
  categoryColor,
  containerRef,
}: BarChartProps) => {
  const { isMobile } = useWindowSize();
  const {
    nivoData,
    keys,
    otherSeriesNames = [],
    otherSeriesData = [],
  } = isStacked
    ? buildStackedNivoData(xLabels, series, showOther)
    : (() => {
        const nivoData: BarDatum[] = xLabels.map((xLabel, xIndex) => {
          const datum: BarDatum = { xLabel };
          series.forEach((s) => {
            datum[s.name] = s.data[xIndex] ?? 0;
          });
          return datum;
        });
        return {
          nivoData,
          keys: series.map((s) => s.name),
          otherSeriesNames: [] as string[],
          otherSeriesData: [] as BarChartSeriesItem[],
        };
      })();

  const topSeriesNames = series
    .map((s) => ({
      name: s.name,
      total: s.data.reduce((sum, v) => sum + Math.abs(v), 0),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, MAX_SERIES)
    .map((s) => s.name);

  const seriesColorMap = new Map<string, string>([
    ...topSeriesNames.map((name, i): [string, string] => [
      name,
      categoryColor[i % categoryColor.length],
    ]),
    [OTHER_KEY, colors.neutral[20]],
  ]);

  const stackedExtremes = isStacked
    ? nivoData.flatMap((datum) => {
        const positiveSum = keys
          .filter((k) => Number(datum[k] ?? 0) > 0)
          .reduce((sum, k) => sum + Number(datum[k] ?? 0), 0);
        const negativeSum = keys
          .filter((k) => Number(datum[k] ?? 0) < 0)
          .reduce((sum, k) => sum + Number(datum[k] ?? 0), 0);
        return [positiveSum, negativeSum];
      })
    : nivoData.flatMap((datum) => keys.map((k) => Number(datum[k] ?? 0)));

  const barMin = Math.min(...stackedExtremes, 0);
  const barMax = Math.max(...stackedExtremes, 0);

  const maxTickInterval = getNiceTickInterval(
    Math.max(Math.abs(barMin), Math.abs(barMax))
  );
  const scaleMin =
    barMin < 0 ? Math.floor(barMin / maxTickInterval) * maxTickInterval : 0;
  const scaleMax =
    barMax > 0 ? Math.ceil(barMax / maxTickInterval) * maxTickInterval : 0;

  const tickValues = generateTickValues(scaleMin, scaleMax, maxTickInterval);

  let tooltipComponent = isPercentage ? PercentageTooltip : Tooltip;
  if (isStacked)
    tooltipComponent = makeStackedTooltip(
      isPercentage,
      otherSeriesData,
      xLabels
    );

  const formatYAxis = isPercentage
    ? (v: number | string) => {
        const n = Number(v);
        return `${n < 0 ? "-" : ""}${Math.abs(n).toFixed(0)}%`;
      }
    : (v: number | string) => formatSignedAxisValue(Number(v));

  const legendKeys = isStacked
    ? Array.from(new Set(keys.map(resolveSeriesName)).values())
    : keys;

  return (
    <div
      ref={containerRef}
      className="t-flex t-flex-col t-gap-4 t-h-full t-border t-border-l t-border-solid t-border-neutral-10 t-rounded-lg t-p-4 sm:t-px-3 t-mr-8 sm:t-mr-0"
    >
      <div style={{ height: 340 }}>
        <ResponsiveBar
          data={nivoData}
          keys={keys}
          indexBy="xLabel"
          margin={{ top: 30, right: 20, bottom: isMobile ? 70 : 40, left: 50 }}
          padding={0.3}
          groupMode={isStacked ? "stacked" : "grouped"}
          valueScale={{ type: "linear", min: scaleMin, max: scaleMax }}
          indexScale={{ type: "band", round: true }}
          gridYValues={tickValues}
          colors={({ id }) => {
            const seriesName = resolveSeriesName(String(id));
            return seriesColorMap.get(seriesName) ?? categoryColor[0];
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: isMobile
              ? MOBILE_TICK_ROTATION
              : xLabels.length > 4
              ? -30
              : 0,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            format: formatYAxis,
            tickValues,
          }}
          enableGridX={false}
          enableGridY
          enableLabel={false}
          tooltip={tooltipComponent}
          theme={{
            grid: {
              line: {
                stroke: colors.neutral[0],
                strokeDasharray: "4 4",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: colors.text[30],
                  fontSize: 12,
                },
              },
            },
          }}
        />
      </div>

      <div className="t-flex t-flex-wrap t-gap-x-4 t-gap-y-2 t-px-4">
        {legendKeys.map((key) => {
          const isOther = key === OTHER_KEY;
          const label =
            isOther && otherSeriesNames.length > 0
              ? `Others (${otherSeriesNames.join(", ")})`
              : key;

          return (
            <div
              key={key}
              className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-60"
            >
              <span
                className="t-h-2.5 t-w-2.5 t-rounded-full t-flex-shrink-0"
                style={{
                  backgroundColor: seriesColorMap.get(key) ?? categoryColor[0],
                }}
              />
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CHART_COLORS = [
  colors.purple[30],
  colors.red[30],
  colors.yellow[30],
  colors.blue[20],
  colors.green[30],
  colors.neutral[20],
];

export const CustomReportBarChart = ({
  chartData,
  containerRef,
}: {
  chartData: CustomReportChartResponse;
  containerRef?: RefObject<HTMLDivElement | null>;
}) => (
  <BarChart
    xLabels={chartData.x_axis.data}
    series={chartData.series}
    isStacked={chartData.series_type === SERIES_MODE.CATEGORIES}
    isPercentage={chartData.analysis_mode === ANALYSIS_MODE.PERCENT}
    showOther={chartData.series_type === SERIES_MODE.CATEGORIES}
    categoryColor={CHART_COLORS}
    containerRef={containerRef}
  />
);
